import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { collection, addDoc } from "firebase/firestore";
import { db } from "../firebase/firebase";
import { scrollWithOffsetLarge } from "../utils/scrollUtils";


import bg2 from "../assets/images/bg/financial-security-calculator-background.webp"

import NavbarTwo from "../components/navbar/navbarTwo"
import FooterThree from "../components/footer/footerThree";
import ScrollTop from "../components/scrollTop";

import { FiArrowRight, FiArrowLeft, FiPlus, FiTrash2, FiEdit } from '../assets/icons/vander'

// Page 1 Component - extracted outside the main component
function Page1({ formData, updateFormData, addChild, updateChild, removeChild, goToPage }) {
    return (
        <div className="card border-0 shadow rounded">
            <div className="card-body p-4">
                <h3 className="mb-4">Let's start with your household. (1 of 6)</h3>

                {/* Name */}
                <div className="mb-4">
                    <label className="form-label fw-semibold">Your name</label>
                    <div className="row">
                        <div className="col-md-4 mb-3">
                            <input 
                                type="text" 
                                className="form-control" 
                                placeholder="First name"
                                value={formData.firstName}
                                onChange={(e) => updateFormData('firstName', e.target.value)}
                                required
                            />
                        </div>
                        <div className="col-md-4 mb-3">
                            <input 
                                type="text" 
                                className="form-control" 
                                placeholder="Last name (optional)"
                                value={formData.lastName}
                                onChange={(e) => updateFormData('lastName', e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                {/* Relationship Status */}
                <div className="mb-4">
                    <label className="form-label fw-semibold">Relationship status</label>
                    <div className="mt-2">
                        <div className="form-check mb-2">
                            <input 
                                className="form-check-input" 
                                type="radio" 
                                name="relationshipStatus" 
                                value="married"
                                checked={formData.relationshipStatus === 'married'}
                                onChange={(e) => updateFormData('relationshipStatus', e.target.value)}
                            />
                            <label className="form-check-label">Married or in a committed long-term partnership</label>
                        </div>
                        <div className="form-check">
                            <input 
                                className="form-check-input" 
                                type="radio" 
                                name="relationshipStatus" 
                                value="single"
                                checked={formData.relationshipStatus === 'single'}
                                onChange={(e) => updateFormData('relationshipStatus', e.target.value)}
                            />
                            <label className="form-check-label">Single / Unpartnered</label>
                        </div>
                    </div>
                </div>

                {/* Children */}
                <div className="mb-4">
                    <label className="form-label fw-semibold">Children</label>
                    <div className="mt-2">
                        <div className="form-check mb-2">
                            <input 
                                className="form-check-input" 
                                type="radio" 
                                name="hasChildren" 
                                value="have"
                                checked={formData.hasChildren === 'have'}
                                onChange={(e) => updateFormData('hasChildren', e.target.value)}
                            />
                            <label className="form-check-label">I have children</label>
                        </div>
                        <div className="form-check mb-2">
                            <input 
                                className="form-check-input" 
                                type="radio" 
                                name="hasChildren" 
                                value="expecting"
                                checked={formData.hasChildren === 'expecting'}
                                onChange={(e) => updateFormData('hasChildren', e.target.value)}
                            />
                            <label className="form-check-label">I'm expecting a child soon</label>
                        </div>
                        <div className="form-check">
                            <input 
                                className="form-check-input" 
                                type="radio" 
                                name="hasChildren" 
                                value="none"
                                checked={formData.hasChildren === 'none'}
                                onChange={(e) => updateFormData('hasChildren', e.target.value)}
                            />
                            <label className="form-check-label">No children / Not expecting</label>
                        </div>
                    </div>
                </div>

                {/* Children Details */}
                {(formData.hasChildren === 'have' || formData.hasChildren === 'expecting') && (
                    <div className="mb-4">
                        <label className="form-label fw-semibold">Children details</label>
                        <small className="text-muted d-block mb-2">Tap +Add to enter multiple kids.</small>
                        
                        {formData.children.map((child, index) => (
                            <div key={index} className="border rounded p-3 mb-2">
                                <div className="row">
                                    <div className="col-md-5 mb-2">
                                        <input 
                                            type="text" 
                                            className="form-control" 
                                            placeholder="Child name"
                                            value={child.name}
                                            onChange={(e) => updateChild(index, 'name', e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-5 mb-2">
                                        <input 
                                            type="number" 
                                            className="form-control" 
                                            placeholder="Birth year (YYYY)"
                                            min="1990"
                                            max="2030"
                                            value={child.birthYear}
                                            onChange={(e) => updateChild(index, 'birthYear', e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-2 mb-2">
                                        <button 
                                            type="button" 
                                            className="btn btn-outline-danger"
                                            onClick={() => removeChild(index)}
                                        >
                                            <FiTrash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                        
                        <button 
                            type="button" 
                            className="btn btn-outline-primary"
                            onClick={addChild}
                        >
                            <FiPlus size={16} className="me-1" /> Add Child
                        </button>
                    </div>
                )}

                {/* Income Section */}
                <div className="mb-4">
                    <label className="form-label fw-semibold">Income Roles</label>
                    {formData.relationshipStatus === 'married' ? (
                        <>
                            <small className="text-muted d-block mb-2">Helps us estimate how much household income you need to replace.</small>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Your annual income (USD)</label>
                                    <input 
                                        type="number" 
                                        className="form-control" 
                                        placeholder="0"
                                        value={formData.yourIncome}
                                        onChange={(e) => updateFormData('yourIncome', e.target.value)}
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Partner's annual income (USD)</label>
                                    <input 
                                        type="number" 
                                        className="form-control" 
                                        placeholder="0"
                                        value={formData.partnerIncome}
                                        onChange={(e) => updateFormData('partnerIncome', e.target.value)}
                                    />
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <small className="text-muted d-block mb-2">This helps us figure out your personal income-replacement need.</small>
                            <div className="mb-3">
                                <label className="form-label">Your annual income (USD)</label>
                                <input 
                                    type="number" 
                                    className="form-control" 
                                    placeholder="0"
                                    value={formData.yourIncome}
                                    onChange={(e) => updateFormData('yourIncome', e.target.value)}
                                />
                            </div>
                        </>
                    )}
                </div>

                {/* Other Dependents */}
                <div className="mb-4">
                    <label className="form-label fw-semibold">Other dependents</label>
                    <p className="text-muted mb-2">Does anyone outside your household rely on you financially (elderly parents, relatives, etc.)?</p>
                    <div className="d-flex gap-4">
                        <div className="form-check">
                            <input 
                                className="form-check-input" 
                                type="checkbox" 
                                id="hasOtherDependents-yes"
                                checked={formData.hasOtherDependents === true}
                                onChange={(e) => updateFormData('hasOtherDependents', e.target.checked ? true : false)}
                            />
                            <label className="form-check-label" htmlFor="hasOtherDependents-yes">
                                <strong>Yes</strong>
                            </label>
                        </div>
                        <div className="form-check">
                            <input 
                                className="form-check-input" 
                                type="checkbox" 
                                id="hasOtherDependents-no"
                                checked={formData.hasOtherDependents === false}
                                onChange={(e) => updateFormData('hasOtherDependents', e.target.checked ? false : true)}
                            />
                            <label className="form-check-label" htmlFor="hasOtherDependents-no">
                                <strong>No</strong>
                            </label>
                        </div>
                    </div>
                    {formData.hasOtherDependents && (
                        <small className="text-success d-block mt-2">Great—we'll include extra support in your plan.</small>
                    )}
                </div>
            </div>
        </div>
    );
}

// Page 2 Component - extracted outside the main component
function Page2({ formData, updateFormData, addDebt, updateDebt, removeDebt }) {
    return (
        <div className="card border-0 shadow rounded">
            <div className="card-body p-4">
                <h3 className="mb-4">Covering your basics. (2 of 6)</h3>
                
                {/* Home Status */}
                <div className="mb-4">
                    <label className="form-label fw-semibold">Home status</label>
                    <div className="mt-2">
                        <div className="form-check form-check-inline">
                            <input 
                                className="form-check-input" 
                                type="radio" 
                                name="homeStatus" 
                                value="own"
                                checked={formData.homeStatus === 'own'}
                                onChange={(e) => updateFormData('homeStatus', e.target.value)}
                            />
                            <label className="form-check-label">Own</label>
                        </div>
                        <div className="form-check form-check-inline">
                            <input 
                                className="form-check-input" 
                                type="radio" 
                                name="homeStatus" 
                                value="rent"
                                checked={formData.homeStatus === 'rent'}
                                onChange={(e) => updateFormData('homeStatus', e.target.value)}
                            />
                            <label className="form-check-label">Rent</label>
                        </div>
                        <div className="form-check form-check-inline">
                            <input 
                                className="form-check-input" 
                                type="radio" 
                                name="homeStatus" 
                                value="other"
                                checked={formData.homeStatus === 'other'}
                                onChange={(e) => updateFormData('homeStatus', e.target.value)}
                            />
                            <label className="form-check-label">Other</label>
                        </div>
                    </div>
                </div>

                {/* Mortgage Balance */}
                {formData.homeStatus === 'own' && (
                    <div className="mb-4">
                        <label className="form-label fw-semibold">Mortgage balance</label>
                        <input 
                            type="number" 
                            className="form-control" 
                            placeholder="What's left on your mortgage? (USD)"
                            value={formData.mortgageBalance}
                            onChange={(e) => updateFormData('mortgageBalance', e.target.value)}
                        />
                    </div>
                )}

                {/* Monthly Housing Cost */}
                {formData.homeStatus !== 'other' && (
                    <div className="mb-4">
                        <label className="form-label fw-semibold">Monthly housing cost</label>
                        <input 
                            type="number" 
                            className="form-control" 
                            placeholder={formData.homeStatus === 'own' 
                                ? "Average monthly housing cost (monthly mortgage payment + HOA dues)"
                                : "How much is your monthly rent? (USD)"
                            }
                            value={formData.monthlyHousingCost}
                            onChange={(e) => updateFormData('monthlyHousingCost', e.target.value)}
                        />
                    </div>
                )}

                {/* Other Debt */}
                <div className="mb-4">
                    <label className="form-label fw-semibold">Other debt</label>
                    <p className="text-muted mb-2">Do you have student loans, car loans, or credit-card debt?</p>
                    <div className="d-flex gap-4">
                        <div className="form-check">
                            <input 
                                className="form-check-input" 
                                type="checkbox" 
                                id="hasOtherDebt-yes"
                                checked={formData.hasOtherDebt === true}
                                onChange={(e) => updateFormData('hasOtherDebt', e.target.checked ? true : false)}
                            />
                            <label className="form-check-label" htmlFor="hasOtherDebt-yes">
                                <strong>Yes</strong>
                            </label>
                        </div>
                        <div className="form-check">
                            <input 
                                className="form-check-input" 
                                type="checkbox" 
                                id="hasOtherDebt-no"
                                checked={formData.hasOtherDebt === false}
                                onChange={(e) => updateFormData('hasOtherDebt', e.target.checked ? false : true)}
                            />
                            <label className="form-check-label" htmlFor="hasOtherDebt-no">
                                <strong>No</strong>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Debt Breakdown */}
                {formData.hasOtherDebt && (
                    <div className="mb-4">
                        <label className="form-label fw-semibold">Debt breakdown</label>
                        <small className="text-muted d-block mb-2">Tap +Add to list each loan or card.</small>
                        
                        {formData.debts.map((debt, index) => (
                            <div key={index} className="border rounded p-3 mb-2">
                                <div className="row">
                                    <div className="col-md-5 mb-2">
                                        <select 
                                            className="form-select"
                                            value={debt.type}
                                            onChange={(e) => updateDebt(index, 'type', e.target.value)}
                                        >
                                            <option value="">Select debt type</option>
                                            <option value="student">Student</option>
                                            <option value="auto">Auto</option>
                                            <option value="credit">Credit card</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>
                                    <div className="col-md-5 mb-2">
                                        <input 
                                            type="number" 
                                            className="form-control" 
                                            placeholder="Remaining balance (USD)"
                                            value={debt.balance}
                                            onChange={(e) => updateDebt(index, 'balance', e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-2 mb-2">
                                        <button 
                                            type="button" 
                                            className="btn btn-outline-danger"
                                            onClick={() => removeDebt(index)}
                                        >
                                            <FiTrash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                        
                        <button 
                            type="button" 
                            className="btn btn-outline-primary mb-3"
                            onClick={addDebt}
                        >
                            <FiPlus size={16} className="me-1" /> Add Debt
                        </button>
                    </div>
                )}

                {/* Debt Coverage Confidence */}
                <div className="mb-4">
                    <label className="form-label fw-semibold">Debt-coverage confidence</label>
                    <p className="text-muted mb-2">
                        {formData.relationshipStatus === 'married' 
                            ? "If something happened to you, could your partner cover these payments alone?"
                            : "If you were to pass away or become disabled, could your estate's assets clear these debts so they wouldn't fall to loved ones?"
                        }
                    </p>
                    <div className="d-flex gap-4">
                        <div className="form-check">
                            <input 
                                className="form-check-input" 
                                type="checkbox" 
                                id="debtCoverageConfidence-yes"
                                checked={formData.debtCoverageConfidence === true}
                                onChange={(e) => updateFormData('debtCoverageConfidence', e.target.checked ? true : false)}
                            />
                            <label className="form-check-label" htmlFor="debtCoverageConfidence-yes">
                                <strong>Yes</strong>
                            </label>
                        </div>
                        <div className="form-check">
                            <input 
                                className="form-check-input" 
                                type="checkbox" 
                                id="debtCoverageConfidence-no"
                                checked={formData.debtCoverageConfidence === false}
                                onChange={(e) => updateFormData('debtCoverageConfidence', e.target.checked ? false : true)}
                            />
                            <label className="form-check-label" htmlFor="debtCoverageConfidence-no">
                                <strong>No</strong>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Page 3 Component - extracted outside the main component
function Page3({ formData, updateFormData, goToPage, addPolicy, updatePolicy, removePolicy }) {
    return (
        <div className="card border-0 shadow rounded">
            <div className="card-body p-4">
                <h3 className="mb-4">Understanding your income. (3 of 6)</h3>
                
                {/* Your Income Display */}
                <div className="mb-4">
                    <div className="alert alert-custom d-flex justify-content-between align-items-center">
                        <span>You told us your income is ${parseFloat(formData.yourIncome || 0).toLocaleString()} per year.</span>
                        <button 
                            type="button" 
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => goToPage(1)}
                        >
                            <FiEdit size={14} className="me-1" /> Edit
                        </button>
                    </div>
                </div>

                {/* Partner Income Display */}
                {formData.relationshipStatus === 'married' && (
                    <div className="mb-4">
                        <div className="alert alert-custom d-flex justify-content-between align-items-center">
                            <span>You told us your partner's income is ${parseFloat(formData.partnerIncome || 0).toLocaleString()} per year.</span>
                            <button 
                                type="button" 
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => goToPage(1)}
                            >
                                <FiEdit size={14} className="me-1" /> Edit
                            </button>
                        </div>
                    </div>
                )}

                {/* Income Replacement Need */}
                <div className="mb-4">
                    <label className="form-label fw-semibold">Income-replacement need</label>
                    <small className="text-muted d-block mb-2">How many years would your family need to maintain today's lifestyle if income stopped?</small>
                    <div className="mt-2">
                        <div className="form-check mb-2">
                            <input 
                                className="form-check-input" 
                                type="radio" 
                                name="incomeReplacementYears" 
                                value="5"
                                checked={formData.incomeReplacementYears === '5'}
                                onChange={(e) => updateFormData('incomeReplacementYears', e.target.value)}
                            />
                            <label className="form-check-label">5 years of income</label>
                        </div>
                        <div className="form-check mb-2">
                            <input 
                                className="form-check-input" 
                                type="radio" 
                                name="incomeReplacementYears" 
                                value="10"
                                checked={formData.incomeReplacementYears === '10'}
                                onChange={(e) => updateFormData('incomeReplacementYears', e.target.value)}
                            />
                            <label className="form-check-label">10 years of income</label>
                        </div>
                        <div className="form-check mb-2">
                            <input 
                                className="form-check-input" 
                                type="radio" 
                                name="incomeReplacementYears" 
                                value="15"
                                checked={formData.incomeReplacementYears === '15'}
                                onChange={(e) => updateFormData('incomeReplacementYears', e.target.value)}
                            />
                            <label className="form-check-label">15 years of income</label>
                        </div>
                        <div className="form-check">
                            <input 
                                className="form-check-input" 
                                type="radio" 
                                name="incomeReplacementYears" 
                                value="custom"
                                checked={formData.incomeReplacementYears === 'custom'}
                                onChange={(e) => updateFormData('incomeReplacementYears', e.target.value)}
                            />
                            <label className="form-check-label">Custom:</label>
                        </div>
                        {formData.incomeReplacementYears === 'custom' && (
                            <div className="ms-4 mt-2">
                                <div className="d-flex align-items-center gap-2">
                                    <input 
                                        type="number" 
                                        className="form-control" 
                                        style={{width: '100px'}}
                                        placeholder="years"
                                        value={formData.customYears}
                                        onChange={(e) => updateFormData('customYears', e.target.value)}
                                    />
                                    <span className="text-muted">years</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Savings & Investments */}
                <div className="mb-4">
                    <label className="form-label fw-semibold">Savings & investments</label>
                    <p className="text-muted mb-2">Do you have cash reserves or investments set aside as a safety net?</p>
                    <div className="d-flex gap-4">
                        <div className="form-check">
                            <input 
                                className="form-check-input" 
                                type="checkbox" 
                                id="hasSavings-yes"
                                checked={formData.hasSavings === true}
                                onChange={(e) => updateFormData('hasSavings', e.target.checked ? true : false)}
                            />
                            <label className="form-check-label" htmlFor="hasSavings-yes">
                                <strong>Yes</strong>
                            </label>
                        </div>
                        <div className="form-check">
                            <input 
                                className="form-check-input" 
                                type="checkbox" 
                                id="hasSavings-no"
                                checked={formData.hasSavings === false}
                                onChange={(e) => updateFormData('hasSavings', e.target.checked ? false : true)}
                            />
                            <label className="form-check-label" htmlFor="hasSavings-no">
                                <strong>No</strong>
                            </label>
                        </div>
                    </div>
                    
                    {formData.hasSavings && (
                        <div className="mt-3">
                            <select 
                                className="form-select"
                                value={formData.savingsAmount}
                                onChange={(e) => updateFormData('savingsAmount', e.target.value)}
                            >
                                <option value="">Select amount range</option>
                                <option value="under-10k">Under $10,000</option>
                                <option value="10k-20k">$10,000 – $20,000</option>
                                <option value="20k-30k">$20,000 – $30,000</option>
                                <option value="30k-40k">$30,000 – $40,000</option>
                                <option value="40k-50k">$40,000 – $50,000</option>
                                <option value="50k-60k">$50,000 – $60,000</option>
                                <option value="60k-70k">$60,000 – $70,000</option>
                                <option value="70k-80k">$70,000 – $80,000</option>
                                <option value="80k-90k">$80,000 – $90,000</option>
                                <option value="90k-100k">$90,000 – $100,000</option>
                                <option value="over-100k">Over $100,000</option>
                            </select>
                        </div>
                    )}
                </div>

                {/* Existing Life Insurance */}
                <div className="mb-4">
                    <label className="form-label fw-semibold">Existing life insurance</label>
                    <p className="text-muted mb-2">Do you already have any life insurance policies (through work or privately)?</p>
                    <div className="d-flex gap-4">
                        <div className="form-check">
                            <input 
                                className="form-check-input" 
                                type="checkbox" 
                                id="hasExistingInsurance-yes"
                                checked={formData.hasExistingInsurance === true}
                                onChange={(e) => updateFormData('hasExistingInsurance', e.target.checked ? true : false)}
                            />
                            <label className="form-check-label" htmlFor="hasExistingInsurance-yes">
                                <strong>Yes</strong>
                            </label>
                        </div>
                        <div className="form-check">
                            <input 
                                className="form-check-input" 
                                type="checkbox" 
                                id="hasExistingInsurance-no"
                                checked={formData.hasExistingInsurance === false}
                                onChange={(e) => updateFormData('hasExistingInsurance', e.target.checked ? false : true)}
                            />
                            <label className="form-check-label" htmlFor="hasExistingInsurance-no">
                                <strong>No</strong>
                            </label>
                        </div>
                    </div>
                    
                    {formData.hasExistingInsurance && (
                        <div className="mt-3">
                            <small className="text-muted d-block mb-2">Tap +Add another to list all your policies.</small>
                            
                            {formData.existingPolicies.map((policy, index) => (
                                <div key={index} className="border rounded p-3 mb-2">
                                    <div className="row">
                                        <div className="col-md-4 mb-2">
                                            <select 
                                                className="form-select"
                                                value={policy.source}
                                                onChange={(e) => updatePolicy(index, 'source', e.target.value)}
                                            >
                                                <option value="">Coverage source</option>
                                                <option value="employer">Employer-Sponsored</option>
                                                <option value="individual">Individually-Owned Policy</option>
                                            </select>
                                        </div>
                                        <div className="col-md-3 mb-2">
                                            <select 
                                                className="form-select"
                                                value={policy.type}
                                                onChange={(e) => updatePolicy(index, 'type', e.target.value)}
                                            >
                                                <option value="">Policy type</option>
                                                <option value="term">Term</option>
                                                <option value="whole">Whole</option>
                                                <option value="other">Other</option>
                                            </select>
                                        </div>
                                        <div className="col-md-3 mb-2">
                                            <input 
                                                type="number" 
                                                className="form-control" 
                                                placeholder="Face amount (USD)"
                                                value={policy.amount}
                                                onChange={(e) => updatePolicy(index, 'amount', e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-2 mb-2">
                                            <button 
                                                type="button" 
                                                className="btn btn-outline-danger"
                                                onClick={() => removePolicy(index)}
                                            >
                                                <FiTrash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            
                            <button 
                                type="button" 
                                className="btn btn-outline-primary"
                                onClick={addPolicy}
                            >
                                <FiPlus size={16} className="me-1" /> Add Policy
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// Page 4 Component - extracted outside the main component
function Page4({ formData, updateFormData, toggleGoal }) {
    return (
        <div className="card border-0 shadow rounded">
            <div className="card-body p-4">
                <h3 className="mb-4">Planning for tomorrow. (4 of 6)</h3>
                
                {/* Education Funding */}
                <div className="mb-4">
                    <label className="form-label fw-semibold">Education funding</label>
                    <p className="text-muted mb-2">Would you like to guarantee community college or university costs are covered for your children?</p>
                    <div className="d-flex gap-4">
                        <div className="form-check">
                            <input 
                                className="form-check-input" 
                                type="checkbox" 
                                id="wantEducationFunding-yes"
                                checked={formData.wantEducationFunding === true}
                                onChange={(e) => updateFormData('wantEducationFunding', e.target.checked ? true : false)}
                            />
                            <label className="form-check-label" htmlFor="wantEducationFunding-yes">
                                <strong>Yes</strong>
                            </label>
                        </div>
                        <div className="form-check">
                            <input 
                                className="form-check-input" 
                                type="checkbox" 
                                id="wantEducationFunding-no"
                                checked={formData.wantEducationFunding === false}
                                onChange={(e) => updateFormData('wantEducationFunding', e.target.checked ? false : true)}
                            />
                            <label className="form-check-label" htmlFor="wantEducationFunding-no">
                                <strong>No</strong>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Years & Type of Funding */}
                {formData.wantEducationFunding && (
                    <div className="mb-4">
                        <label className="form-label fw-semibold">Years & type of funding</label>
                        <small className="text-muted d-block mb-2">How many years of schooling would you like fully funded per child? (note: annual costs above are the average US costs for the 2025-2026 school year.)</small>
                        <div className="mt-2">
                            <div className="form-check mb-2">
                                <input 
                                    className="form-check-input" 
                                    type="radio" 
                                    name="educationType" 
                                    value="community"
                                    checked={formData.educationType === 'community'}
                                    onChange={(e) => updateFormData('educationType', e.target.value)}
                                />
                                <label className="form-check-label">Community College (2 years, ~$6,000/yr.)</label>
                            </div>
                            <div className="form-check mb-2">
                                <input 
                                    className="form-check-input" 
                                    type="radio" 
                                    name="educationType" 
                                    value="university-instate"
                                    checked={formData.educationType === 'university-instate'}
                                    onChange={(e) => updateFormData('educationType', e.target.value)}
                                />
                                <label className="form-check-label">University (4 years, ~$25,000/yr. in-state)</label>
                            </div>
                            <div className="form-check">
                                <input 
                                    className="form-check-input" 
                                    type="radio" 
                                    name="educationType" 
                                    value="university-outstate"
                                    checked={formData.educationType === 'university-outstate'}
                                    onChange={(e) => updateFormData('educationType', e.target.value)}
                                />
                                <label className="form-check-label">University (4 years, ~$49,000/yr. out-of-state)</label>
                            </div>
                        </div>
                    </div>
                )}

                {/* Big Future Goals */}
                <div className="mb-4">
                    <label className="form-label fw-semibold">Big future goals</label>
                    <small className="text-muted d-block mb-2">Select any major life goals you're planning for.</small>
                    <div className="mt-2">
                        {[
                            { value: 'home', label: 'Buy a home / larger home' },
                            { value: 'cash-reserve', label: 'Build a cash reserve you can borrow against without going into debt' },
                            { value: 'aging-parents', label: 'Care for aging parent(s)' },
                            { value: 'business', label: 'Start a business' }
                        ].map((goal) => (
                            <div key={goal.value} className="form-check mb-2">
                                <input 
                                    className="form-check-input" 
                                    type="checkbox" 
                                    checked={formData.bigFutureGoals.includes(goal.value)}
                                    onChange={() => toggleGoal(goal.value)}
                                />
                                <label className="form-check-label">{goal.label}</label>
                            </div>
                        ))}
                        <div className="mt-2">
                            <input 
                                type="text" 
                                className="form-control" 
                                placeholder="Other: specify"
                                onChange={(e) => {
                                    if (e.target.value && !formData.bigFutureGoals.includes(`other-${e.target.value}`)) {
                                        toggleGoal(`other-${e.target.value}`);
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Page 5 Component - extracted outside the main component
function Page5({ formData, updateFormData, calculateEstimates, nextPage, setCurrentPage }) {
    const estimates = calculateEstimates();
    const userName = formData.firstName ? formData.firstName : 'there';
    
    return (
        <div className="card border-0 shadow rounded">
            <div className="card-body p-4">
                <h3 className="mb-4">Facing the what-ifs & your cost snapshot. (5 of 6)</h3>

                {/* Partner's Financial Runway */}
                {formData.relationshipStatus === 'married' && (
                    <div className="mb-4">
                        <label className="form-label fw-semibold">Partner's financial runway</label>
                        <p className="text-muted">If something happened to you tomorrow and your income stopped immediately, how long could your partner maintain your household's living expenses (mortgage/rent, utilities, groceries, etc.) on their own?</p>
                        <div className="mt-2">
                            <div className="form-check mb-2">
                                <input 
                                    className="form-check-input" 
                                    type="radio" 
                                    name="partnerFinancialRunway" 
                                    value="less-6-months"
                                    checked={formData.partnerFinancialRunway === 'less-6-months'}
                                    onChange={(e) => updateFormData('partnerFinancialRunway', e.target.value)}
                                />
                                <label className="form-check-label">Less than 6 months</label>
                            </div>
                            <div className="form-check mb-2">
                                <input 
                                    className="form-check-input" 
                                    type="radio" 
                                    name="partnerFinancialRunway" 
                                    value="6-12-months"
                                    checked={formData.partnerFinancialRunway === '6-12-months'}
                                    onChange={(e) => updateFormData('partnerFinancialRunway', e.target.value)}
                                />
                                <label className="form-check-label">6 – 12 months</label>
                            </div>
                            <div className="form-check mb-2">
                                <input 
                                    className="form-check-input" 
                                    type="radio" 
                                    name="partnerFinancialRunway" 
                                    value="more-1-year"
                                    checked={formData.partnerFinancialRunway === 'more-1-year'}
                                    onChange={(e) => updateFormData('partnerFinancialRunway', e.target.value)}
                                />
                                <label className="form-check-label">More than 1 year</label>
                            </div>
                            <div className="form-check">
                                <input 
                                    className="form-check-input" 
                                    type="radio" 
                                    name="partnerFinancialRunway" 
                                    value="not-sure"
                                    checked={formData.partnerFinancialRunway === 'not-sure'}
                                    onChange={(e) => updateFormData('partnerFinancialRunway', e.target.value)}
                                />
                                <label className="form-check-label">Not sure – help me estimate</label>
                            </div>
                        </div>
                    </div>
                )}

                {/* Home Stability */}
                <div className="mb-4">
                    <label className="form-label fw-semibold">Home stability</label>
                    <p className="text-muted mb-2">If you were to pass away today, would you want your family to remain in your current home rather than being forced to move because of financial constraints?</p>
                    <div className="d-flex gap-4">
                        <div className="form-check">
                            <input 
                                className="form-check-input" 
                                type="checkbox" 
                                id="wantHomeStability-yes"
                                checked={formData.wantHomeStability === true}
                                onChange={(e) => updateFormData('wantHomeStability', e.target.checked ? true : false)}
                            />
                            <label className="form-check-label" htmlFor="wantHomeStability-yes">
                                <strong>Yes</strong>
                            </label>
                        </div>
                        <div className="form-check">
                            <input 
                                className="form-check-input" 
                                type="checkbox" 
                                id="wantHomeStability-no"
                                checked={formData.wantHomeStability === false}
                                onChange={(e) => updateFormData('wantHomeStability', e.target.checked ? false : true)}
                            />
                            <label className="form-check-label" htmlFor="wantHomeStability-no">
                                <strong>No</strong>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Personalized Cost Estimates */}
                <div className="mb-4">
                    <h4 className="fw-semibold mb-3">{userName !== 'there' ? `${userName}, here are your` : 'Your'} personalized cost estimates</h4>
                    <p className="text-muted mb-3">Based on all your answers so far, here's what your family would need if loss or disability struck:</p>
                    
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <div className="card bg-light">
                                <div className="card-body">
                                    <h6 className="card-title">Monthly Obligations</h6>
                                    <p className="mb-1">Housing (mortgage/rent + HOA): <strong>${estimates.monthlyHousing.toLocaleString()}</strong></p>
                                    <p className="mb-1">Other debt payments: <strong>${estimates.monthlyDebt.toLocaleString()}</strong></p>
                                    <hr />
                                    <p className="mb-0"><strong>Total Monthly: ${estimates.totalMonthly.toLocaleString()}</strong></p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="col-md-6 mb-3">
                            <div className="card bg-light">
                                <div className="card-body">
                                    <h6 className="card-title">Income Replacement</h6>
                                    <p className="mb-1">You: <strong>${estimates.yourReplacementNeed.toLocaleString()}</strong></p>
                                    {formData.relationshipStatus === 'married' && (
                                        <p className="mb-1">Partner: <strong>${estimates.partnerReplacementNeed.toLocaleString()}</strong></p>
                                    )}
                                    <hr />
                                    <p className="mb-0"><strong>Total Income Replacement: ${estimates.totalIncomeReplacement.toLocaleString()}</strong></p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="col-md-6 mb-3">
                            <div className="card bg-light">
                                <div className="card-body">
                                    <h6 className="card-title">Education Funding</h6>
                                    <p className="mb-0">
                                        <strong>${estimates.educationCost.toLocaleString()}</strong>
                                        {formData.children.length > 0 && formData.wantEducationFunding && (
                                            <small className="d-block text-muted">
                                                {formData.children.length} child{formData.children.length > 1 ? 'ren' : ''} × {formData.educationType}
                                            </small>
                                        )}
                                    </p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="col-md-6 mb-3">
                            <div className="card bg-light">
                                <div className="card-body">
                                    <h6 className="card-title">Big Future Goals</h6>
                                    <p className="mb-0"><strong>${estimates.totalGoalsCost.toLocaleString()}</strong></p>
                                    {formData.bigFutureGoals.length > 0 && (
                                        <small className="d-block text-muted">
                                            {formData.bigFutureGoals.length} goal{formData.bigFutureGoals.length > 1 ? 's' : ''} selected
                                        </small>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="alert alert-primary mt-3">
                        <h5 className="alert-heading">Estimated Total Protection Needed</h5>
                        <h3 className="mb-0">${estimates.totalProtectionNeeded.toLocaleString()}</h3>
                    </div>
                </div>

                {/* What's Next */}
                <div className="mb-4">
                    <h5 className="fw-semibold mb-3">What's next?</h5>
                    <div className="d-grid gap-2 d-md-flex">
                        <button 
                            type="button" 
                            className="btn btn-primary"
                            onClick={nextPage}
                        >
                            Looks good—continue <FiArrowRight className="ms-1" />
                        </button>
                        <button 
                            type="button" 
                            className="btn btn-outline-secondary"
                            onClick={() => setCurrentPage(1)}
                        >
                            I'd like to tweak my answers
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Page 6 Component - extracted outside the main component
function Page6({ formData, updateFormData, handleSubmit, isSubmitting }) {
    return (
        <div className="card border-0 shadow rounded">
            <div className="card-body p-4">
                <h3 className="mb-4">Your protection style. (6 of 6)</h3>
                
                {/* Coverage Scope */}
                <div className="mb-4">
                    <label className="form-label fw-semibold">Coverage scope</label>
                    <div className="mt-2">
                        <div className="form-check mb-2">
                            <input 
                                className="form-check-input" 
                                type="radio" 
                                name="coverageScope" 
                                value="essentials"
                                checked={formData.coverageScope === 'essentials'}
                                onChange={(e) => updateFormData('coverageScope', e.target.value)}
                            />
                            <label className="form-check-label">Essentials only (mortgage + education)</label>
                        </div>
                        <div className="form-check mb-2">
                            <input 
                                className="form-check-input" 
                                type="radio" 
                                name="coverageScope" 
                                value="full"
                                checked={formData.coverageScope === 'full'}
                                onChange={(e) => updateFormData('coverageScope', e.target.value)}
                            />
                            <label className="form-check-label">Full protection (income, debts, long-term goals)</label>
                        </div>
                        <div className="form-check">
                            <input 
                                className="form-check-input" 
                                type="radio" 
                                name="coverageScope" 
                                value="unsure"
                                checked={formData.coverageScope === 'unsure'}
                                onChange={(e) => updateFormData('coverageScope', e.target.value)}
                            />
                            <label className="form-check-label">Unsure – show me options</label>
                        </div>
                    </div>
                </div>

                {/* Disability Buffer */}
                <div className="mb-4">
                    <label className="form-label fw-semibold">Disability buffer</label>
                    <p className="text-muted mb-2">If you couldn't work for six months or more due to illness or injury, could you cover your household bills and living expenses?</p>
                    <div className="d-flex gap-4">
                        <div className="form-check">
                            <input 
                                className="form-check-input" 
                                type="checkbox" 
                                id="canCoverDisability-yes"
                                checked={formData.canCoverDisability === true}
                                onChange={(e) => updateFormData('canCoverDisability', e.target.checked ? true : false)}
                            />
                            <label className="form-check-label" htmlFor="canCoverDisability-yes">
                                <strong>Yes</strong>
                            </label>
                        </div>
                        <div className="form-check">
                            <input 
                                className="form-check-input" 
                                type="checkbox" 
                                id="canCoverDisability-no"
                                checked={formData.canCoverDisability === false}
                                onChange={(e) => updateFormData('canCoverDisability', e.target.checked ? false : true)}
                            />
                            <label className="form-check-label" htmlFor="canCoverDisability-no">
                                <strong>No</strong>
                            </label>
                        </div>
                    </div>
                    {!formData.canCoverDisability && (
                        <small className="text-info d-block mt-2">We'll include a disability rider recommendation in your plan.</small>
                    )}
                </div>

                {/* Policy Type Preference */}
                <div className="mb-4">
                    <label className="form-label fw-semibold">Policy type preference</label>
                    <p className="text-muted">Which type of life insurance policy are you most comfortable with?</p>
                    <div className="mt-2">
                        <div className="form-check mb-2">
                            <input 
                                className="form-check-input" 
                                type="radio" 
                                name="policyTypePreference" 
                                value="term"
                                checked={formData.policyTypePreference === 'term'}
                                onChange={(e) => updateFormData('policyTypePreference', e.target.value)}
                            />
                            <label className="form-check-label">Term life (lower cost, coverage for a set period)</label>
                        </div>
                        <div className="form-check mb-2">
                            <input 
                                className="form-check-input" 
                                type="radio" 
                                name="policyTypePreference" 
                                value="whole"
                                checked={formData.policyTypePreference === 'whole'}
                                onChange={(e) => updateFormData('policyTypePreference', e.target.value)}
                            />
                            <label className="form-check-label">Whole life (permanent coverage + cash value)</label>
                        </div>
                        <div className="form-check">
                            <input 
                                className="form-check-input" 
                                type="radio" 
                                name="policyTypePreference" 
                                value="unsure"
                                checked={formData.policyTypePreference === 'unsure'}
                                onChange={(e) => updateFormData('policyTypePreference', e.target.value)}
                            />
                            <label className="form-check-label">Not sure – help me decide</label>
                        </div>
                    </div>
                </div>

                {/* Contact Information */}
                <div className="mb-4">
                    <h5 className="fw-semibold mb-3">Contact Information</h5>
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Email Address <span className="text-danger">*</span></label>
                            <input 
                                type="email" 
                                className="form-control" 
                                placeholder="your@email.com"
                                value={formData.email}
                                onChange={(e) => updateFormData('email', e.target.value)}
                                required
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Phone Number <span className="text-muted">(optional)</span></label>
                            <input 
                                type="tel" 
                                className="form-control" 
                                placeholder="(555) 123-4567"
                                value={formData.phone}
                                onChange={(e) => updateFormData('phone', e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                {/* Final CTA */}
                <div className="d-grid">
                    <button 
                        type="button" 
                        className="btn btn-primary btn-lg"
                        onClick={handleSubmit}
                        disabled={isSubmitting || !formData.email}
                    >
                        {isSubmitting ? 'Submitting...' : 'Show me my tailored protection plan →'}
                    </button>
                    <div style={{display:"flex", alignItems:"center", flexDirection:"column"}}>
                        <p className="text-muted small mt-4 mb-0">By submitting this form, you agree to be contacted by David Scott Smith, an independent insurance broker (licensed in VA, NC, SC, TN, and GA) and affiliate of Symmetry Financial Group. You may be contacted by phone, text message, or email at the number and/or email address you provide, even if your number is on a state or federal Do Not Call list. Message and data rates may apply. You may opt out at any time.</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function FinancialSecurityCalculator() {
    const [currentPage, setCurrentPage] = useState(1);
    const [formData, setFormData] = useState({
        // Page 1 - Family & Life Context
        firstName: '',
        lastName: '',
        relationshipStatus: '',
        hasChildren: '',
        children: [],
        yourIncome: '',
        partnerIncome: '',
        hasOtherDependents: false,
        
        // Page 2 - Living Situation & Debt
        homeStatus: '',
        mortgageBalance: '',
        monthlyHousingCost: '',
        hasOtherDebt: false,
        debts: [],
        debtCoverageConfidence: false,
        
        // Page 3 - Income & Financial Stability
        incomeReplacementYears: '',
        customYears: '',
        hasSavings: false,
        savingsAmount: '',
        hasExistingInsurance: false,
        existingPolicies: [],
        
        // Page 4 - Long-term Goals & Commitments
        wantEducationFunding: false,
        educationType: '',
        bigFutureGoals: [],
        
        // Page 5 - Emotional Reality & Estimated Needs
        partnerFinancialRunway: '',
        wantHomeStability: false,
        
        // Page 6 - Protection Preferences
        coverageScope: '',
        canCoverDisability: false,
        policyTypePreference: '',
        
        // Contact Information
        email: '',
        phone: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    // Scroll to top when form is successfully submitted
    useEffect(() => {
        if (submitted) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [submitted]);

    const updateFormData = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const addChild = () => {
        setFormData(prev => ({
            ...prev,
            children: [...prev.children, { name: '', birthYear: '' }]
        }));
    };

    const updateChild = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            children: prev.children.map((child, i) => 
                i === index ? { ...child, [field]: value } : child
            )
        }));
    };

    const removeChild = (index) => {
        setFormData(prev => ({
            ...prev,
            children: prev.children.filter((_, i) => i !== index)
        }));
    };

    const addDebt = () => {
        setFormData(prev => ({
            ...prev,
            debts: [...prev.debts, { type: '', balance: '' }]
        }));
    };

    const updateDebt = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            debts: prev.debts.map((debt, i) => 
                i === index ? { ...debt, [field]: value } : debt
            )
        }));
    };

    const removeDebt = (index) => {
        setFormData(prev => ({
            ...prev,
            debts: prev.debts.filter((_, i) => i !== index)
        }));
    };

    const addPolicy = () => {
        setFormData(prev => ({
            ...prev,
            existingPolicies: [...prev.existingPolicies, { source: '', type: '', amount: '' }]
        }));
    };

    const updatePolicy = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            existingPolicies: prev.existingPolicies.map((policy, i) => 
                i === index ? { ...policy, [field]: value } : policy
            )
        }));
    };

    const removePolicy = (index) => {
        setFormData(prev => ({
            ...prev,
            existingPolicies: prev.existingPolicies.filter((_, i) => i !== index)
        }));
    };

    const toggleGoal = (goal) => {
        setFormData(prev => ({
            ...prev,
            bigFutureGoals: prev.bigFutureGoals.includes(goal)
                ? prev.bigFutureGoals.filter(g => g !== goal)
                : [...prev.bigFutureGoals, goal]
        }));
    };

    const calculateEstimates = () => {
        const currentYear = new Date().getFullYear();
        
        // Monthly Obligations
        const monthlyHousing = parseFloat(formData.monthlyHousingCost) || 0;
        const monthlyDebt = formData.debts.reduce((sum, debt) => {
            const balance = parseFloat(debt.balance) || 0;
            return sum + (balance * 0.02); // Assuming 2% as minimum monthly payment
        }, 0);
        const totalMonthly = monthlyHousing + monthlyDebt;

        // Income Replacement
        const yourIncome = parseFloat(formData.yourIncome) || 0;
        const partnerIncome = parseFloat(formData.partnerIncome) || 0;
        const years = formData.incomeReplacementYears === 'custom' 
            ? parseFloat(formData.customYears) || 10 
            : parseFloat(formData.incomeReplacementYears) || 10;
        
        const yourReplacementNeed = yourIncome * years;
        const partnerReplacementNeed = formData.relationshipStatus === 'married' ? partnerIncome * years : 0;
        const totalIncomeReplacement = yourReplacementNeed + partnerReplacementNeed;

        // Education Funding
        let educationCost = 0;
        if (formData.wantEducationFunding && formData.children.length > 0) {
            const costPerYear = formData.educationType === 'community' ? 6000 : 
                               formData.educationType === 'university-instate' ? 25000 : 49000;
            const yearsOfSchool = formData.educationType === 'community' ? 2 : 4;
            educationCost = formData.children.length * costPerYear * yearsOfSchool;
        }

        // Big Future Goals (rough estimates)
        const goalCosts = {
            'home': 75000,
            'cash-reserve': 25000,
            'aging-parents': 100000,
            'business': 100000
        };
        const totalGoalsCost = formData.bigFutureGoals.reduce((sum, goal) => {
            return sum + (goalCosts[goal] || 0);
        }, 0);

        const totalProtectionNeeded = (totalMonthly * 12 * 5) + totalIncomeReplacement + educationCost + totalGoalsCost;

        return {
            monthlyHousing,
            monthlyDebt,
            totalMonthly,
            yourReplacementNeed,
            partnerReplacementNeed,
            totalIncomeReplacement,
            educationCost,
            totalGoalsCost,
            totalProtectionNeeded
        };
    };

    const handleSubmit = async () => {
        if (!formData.email) {
            alert('Please provide your email address.');
            return;
        }

        setIsSubmitting(true);
        
        try {
            const estimates = calculateEstimates();
            const submissionData = {
                ...formData,
                estimates,
                'contact-opt-in': true,
                submittedAt: new Date(),
                timestamp: Date.now()
            };

            await addDoc(collection(db, 'financial-security-calculator'), submissionData);
            setSubmitted(true);
        } catch (error) {
            console.error('Error submitting form:', error);
            alert('There was an error submitting your information. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const nextPage = () => {
        if (currentPage < 6) {
            setCurrentPage(currentPage + 1);
            const progressElement = document.getElementById('progress-bar-section');
            if (progressElement) {
                scrollWithOffsetLarge(progressElement);
            }
        }
    };

    const prevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
            const progressElement = document.getElementById('progress-bar-section');
            if (progressElement) {
                scrollWithOffsetLarge(progressElement);
            }
        }
    };

    const goToPage = (page) => {
        setCurrentPage(page);
        const progressElement = document.getElementById('progress-bar-section');
        if (progressElement) {
            scrollWithOffsetLarge(progressElement);
        }
    };

    if (submitted) {
        const estimates = calculateEstimates();
        const userName = formData.firstName ? formData.firstName : '';
        
        return (
            <>
                <style>{`
                    @media print {
                        .no-print {
                            display: none !important;
                        }
                        .bg-half-170 {
                            display: none !important;
                        }
                        body {
                            font-size: 14px;
                            line-height: 1.3;
                        }
                        .card {
                            box-shadow: none !important;
                            border: 1px solid #ddd !important;
                            margin-bottom: 10px !important;
                        }
                        .card-body {
                            padding: 15px !important;
                        }
                        .row {
                            margin-bottom: 8px !important;
                        }
                        .col-md-6, .col-md-4 {
                            margin-bottom: 8px !important;
                        }
                        .alert {
                            padding: 12px !important;
                            margin: 10px 0 !important;
                        }
                        .border-start {
                            padding-left: 12px !important;
                        }
                        .text-center {
                            margin-bottom: 8px !important;
                        }
                        h2 {
                            margin-bottom: 8px !important;
                            font-size: 22px !important;
                        }
                        h4 {
                            margin-bottom: 8px !important;
                            font-size: 18px !important;
                        }
                        h5, h6 {
                            margin-bottom: 6px !important;
                            font-size: 16px !important;
                        }
                        p {
                            margin-bottom: 4px !important;
                        }
                        .lead {
                            font-size: 16px !important;
                            margin-bottom: 8px !important;
                        }
                        .rounded-circle {
                            margin-bottom: 5px !important;
                            width: 35px !important;
                            height: 35px !important;
                        }
                        small {
                            font-size: 12px !important;
                        }
                        .page-break-after {
                            page-break-after: always !important;
                        }
                        .page-break-before {
                            page-break-before: always !important;
                        }
                        .print-horizontal {
                            display: flex !important;
                            flex-direction: row !important;
                            gap: 20px !important;
                        }
                        .print-horizontal > div {
                            flex: 1 !important;
                        }
                        .container {
                            max-width: 100% !important;
                            padding: 0 10px !important;
                        }
                        .col-lg-10 {
                            max-width: 100% !important;
                        }
                        .mb-5 {
                            margin-bottom: 15px !important;
                        }
                        .mb-4 {
                            margin-bottom: 10px !important;
                        }
                        .mb-3 {
                            margin-bottom: 8px !important;
                        }
                        .mt-2, .my-2 {
                            margin-top: 4px !important;
                        }
                        hr {
                            margin: 6px 0 !important;
                        }
                    }
                `}</style>
                <div className="no-print">
                    <NavbarTwo navClass="defaultscroll sticky" manuClass="navigation-menu nav-right nav-light"/>
                </div>
                
                <section className="bg-half-170 d-table w-100 no-print" style={{backgroundImage:`url(${bg2})`, backgroundPosition:'center'}}>
                    <div className="bg-overlay bg-gradient-overlay"></div>
                    <div className="container">
                        <div className="row mt-5 justify-content-center">
                            <div className="col-12">
                                <div className="title-heading text-center">
                                    <small className="text-white-50 mb-1 fw-medium text-uppercase mx-auto">Financial Security Report</small>
                                    <h5 className="heading fw-semibold mb-0 page-heading text-white title-dark">Your Personalized Protection Plan</h5>
                                </div>
                            </div>
                        </div>

                        <div className="position-middle-bottom">
                            <nav aria-label="breadcrumb" className="d-block">
                                <ul className="breadcrumb breadcrumb-muted mb-0 p-0">
                                    <li className="breadcrumb-item"><Link to="/">HOME</Link></li>
                                    <li className="breadcrumb-item"><Link to="/financial-security-calculator">CALCULATOR</Link></li>
                                    <li className="breadcrumb-item active" aria-current="page">REPORT</li>
                                </ul>
                            </nav>
                        </div>
                    </div>
                </section>

                <section className="section">
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-lg-10">
                                <div className="card border-0 shadow rounded">
                                    <div className="card-body p-5">
                                        <div className="text-center mb-4">
                                            <h2 className="fw-bold text-success mb-2">Thank You{userName ? `, ${userName}` : ''}!</h2>
                                            <p className="lead text-muted">Your personalized financial security report is ready.</p>
                                        </div>

                                        {/* Financial Summary */}
                                        <div className="mb-5">
                                            <h4 className="fw-semibold mb-4 text-center">Your Financial Security Summary</h4>
                                            
                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <div className="card bg-light border-0">
                                                        <div className="card-body">
                                                            <h6 className="card-title text-primary">Monthly Obligations</h6>
                                                            <p className="mb-1">Housing: <strong>${estimates.monthlyHousing.toLocaleString()}</strong></p>
                                                            <p className="mb-1">Other debt payments: <strong>${estimates.monthlyDebt.toLocaleString()}</strong></p>
                                                            <hr className="my-2" />
                                                            <p className="mb-0 fw-bold">Total Monthly: ${estimates.totalMonthly.toLocaleString()}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                <div className="col-md-6 mb-3">
                                                    <div className="card bg-light border-0">
                                                        <div className="card-body">
                                                            <h6 className="card-title text-primary">Income Replacement</h6>
                                                            <p className="mb-1">Your income protection: <strong>${estimates.yourReplacementNeed.toLocaleString()}</strong></p>
                                                            {formData.relationshipStatus === 'married' && estimates.partnerReplacementNeed > 0 && (
                                                                <p className="mb-1">Partner income protection: <strong>${estimates.partnerReplacementNeed.toLocaleString()}</strong></p>
                                                            )}
                                                            <hr className="my-2" />
                                                            <p className="mb-0 fw-bold">Total Income Protection: ${estimates.totalIncomeReplacement.toLocaleString()}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                <div className="col-md-6 mb-3">
                                                    <div className="card bg-light border-0">
                                                        <div className="card-body">
                                                            <h6 className="card-title text-primary">Education Funding</h6>
                                                            <p className="mb-0 fw-bold">${estimates.educationCost.toLocaleString()}</p>
                                                            {formData.children.length > 0 && formData.wantEducationFunding && (
                                                                <small className="text-muted">
                                                                    {formData.children.length} child{formData.children.length > 1 ? 'ren' : ''} × {
                                                                        formData.educationType === 'community' ? 'Community College' :
                                                                        formData.educationType === 'university-instate' ? 'University (In-State)' :
                                                                        formData.educationType === 'university-outstate' ? 'University (Out-of-State)' : 'Education'
                                                                    }
                                                                </small>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                <div className="col-md-6 mb-3">
                                                    <div className="card bg-light border-0">
                                                        <div className="card-body">
                                                            <h6 className="card-title text-primary">Future Goals</h6>
                                                            <p className="mb-0 fw-bold">${estimates.totalGoalsCost.toLocaleString()}</p>
                                                            {formData.bigFutureGoals.length > 0 ? (
                                                                <small className="text-muted">
                                                                    {formData.bigFutureGoals.map(goal => {
                                                                        const goalLabels = {
                                                                            'home': 'Buy a home / larger home',
                                                                            'cash-reserve': 'Build a cash reserve',
                                                                            'aging-parents': 'Care for aging parent(s)',
                                                                            'business': 'Start a business'
                                                                        };
                                                                        return goalLabels[goal] || goal.replace('other-', '');
                                                                    }).join(', ')}
                                                                </small>
                                                            ) : (
                                                                <small className="text-muted">No specific goals selected</small>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            
                            <div className="alert alert-success border-0 mt-4">
                                <div className="text-center">
                                    <h5 className="alert-heading fw-bold mb-2 text-white">Recommended Total Protection</h5>
                                    <h2 className="mb-0 text-white fw-bold">${estimates.totalProtectionNeeded.toLocaleString()}</h2>
                                    <small className="text-white">Based on your personalized assessment</small>
                                </div>
                            </div>
                        </div>                                        {/* Family Overview */}
                                        <div className="row mb-4 print-horizontal">
                                            <div className="col-md-6">
                                                <div className="border-start border-4 border-primary ps-3">
                                                    <h6 className="fw-semibold mb-2">Household Overview</h6>
                                                    <p className="mb-1">Status: <span className="fw-medium">
                                                        {formData.relationshipStatus === 'married' ? 'Married/Partnered' : 'Single'}
                                                    </span></p>
                                                    <p className="mb-1">Children: <span className="fw-medium">
                                                        {formData.hasChildren === 'have' ? `${formData.children.length} child${formData.children.length > 1 ? 'ren' : ''}` :
                                                         formData.hasChildren === 'expecting' ? 'Expecting' : 'None'}
                                                    </span></p>
                                                    <p className="mb-1">Annual Income: <span className="fw-medium">
                                                        ${(parseFloat(formData.yourIncome) + parseFloat(formData.partnerIncome || 0)).toLocaleString()}
                                                    </span></p>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="border-start border-4 border-success ps-3">
                                                    <h6 className="fw-semibold mb-2">Protection Preferences</h6>
                                                    <p className="mb-1">Coverage Scope: <span className="fw-medium">
                                                        {formData.coverageScope === 'essentials' ? 'Essentials Only' :
                                                         formData.coverageScope === 'full' ? 'Full Protection' : 'Needs Consultation'}
                                                    </span></p>
                                                    <p className="mb-1">Policy Type: <span className="fw-medium">
                                                        {formData.policyTypePreference === 'term' ? 'Term Life' :
                                                         formData.policyTypePreference === 'whole' ? 'Whole Life' : 'Needs Consultation'}
                                                    </span></p>
                                                    <p className="mb-1">Disability Protection: <span className="fw-medium">
                                                        {formData.canCoverDisability ? 'Self-Sufficient' : 'Recommended'}
                                                    </span></p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* What's Next */}
                                        <div className="text-center mb-4 page-break-before">
                                            <h5 className="fw-semibold mb-3">What Happens Next?</h5>
                                            <div className="row">
                                                <div className="col-md-4 mb-3">
                                                    <div className="text-center">
                                                        <div className="rounded-circle bg-primary text-white d-inline-flex align-items-center justify-content-center" style={{width: '50px', height: '50px'}}>
                                                            <span className="fw-bold">1</span>
                                                        </div>
                                                        <h6 className="mt-2 mb-1">Review & Analysis</h6>
                                                        <small className="text-muted">Our team reviews your assessment within 24 hours</small>
                                                    </div>
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <div className="text-center">
                                                        <div className="rounded-circle bg-success text-white d-inline-flex align-items-center justify-content-center" style={{width: '50px', height: '50px'}}>
                                                            <span className="fw-bold">2</span>
                                                        </div>
                                                        <h6 className="mt-2 mb-1">Personalized Plan</h6>
                                                        <small className="text-muted">We create tailored protection recommendations</small>
                                                    </div>
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <div className="text-center">
                                                        <div className="rounded-circle bg-info text-white d-inline-flex align-items-center justify-content-center" style={{width: '50px', height: '50px'}}>
                                                            <span className="fw-bold">3</span>
                                                        </div>
                                                        <h6 className="mt-2 mb-1">Follow-Up Contact</h6>
                                                        <small className="text-muted">We reach out within 1-2 business days</small>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Contact Info Confirmation */}
                                        <div className="alert alert-light border mb-4">
                                            <h6 className="fw-semibold mb-2">We'll contact you at:</h6>
                                            <p className="mb-1">📧 Email: <span className="fw-medium">{formData.email}</span></p>
                                            {formData.phone && (
                                                <p className="mb-1">📞 Phone: <span className="fw-medium">{formData.phone}</span></p>
                                            )}
                                        </div>

                                        {/* Actions */}
                                        <div className="text-center no-print">
                                            <Link to="/" className="btn btn-primary btn-lg me-3">
                                                Return to Home
                                            </Link>
                                            <button 
                                                className="btn btn-outline-secondary"
                                                onClick={() => window.print()}
                                            >
                                                Print This Report
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <div className="no-print">
                    <FooterThree/>
                    <ScrollTop/>
                </div>
            </>
        );
    }

    const getCurrentPageComponent = () => {
        switch (currentPage) {
            case 1: 
                return (
                    <Page1 
                        formData={formData}
                        updateFormData={updateFormData}
                        addChild={addChild}
                        updateChild={updateChild}
                        removeChild={removeChild}
                        goToPage={goToPage}
                    />
                );
            case 2: 
                return (
                    <Page2 
                        formData={formData}
                        updateFormData={updateFormData}
                        addDebt={addDebt}
                        updateDebt={updateDebt}
                        removeDebt={removeDebt}
                    />
                );
            case 3: 
                return (
                    <Page3 
                        formData={formData}
                        updateFormData={updateFormData}
                        goToPage={goToPage}
                        addPolicy={addPolicy}
                        updatePolicy={updatePolicy}
                        removePolicy={removePolicy}
                    />
                );
            case 4: 
                return (
                    <Page4 
                        formData={formData}
                        updateFormData={updateFormData}
                        toggleGoal={toggleGoal}
                    />
                );
            case 5: 
                return (
                    <Page5 
                        formData={formData}
                        updateFormData={updateFormData}
                        calculateEstimates={calculateEstimates}
                        nextPage={nextPage}
                        setCurrentPage={setCurrentPage}
                    />
                );
            case 6: 
                return (
                    <Page6 
                        formData={formData}
                        updateFormData={updateFormData}
                        handleSubmit={handleSubmit}
                        isSubmitting={isSubmitting}
                    />
                );
            default: 
                return (
                    <Page1 
                        formData={formData}
                        updateFormData={updateFormData}
                        addChild={addChild}
                        updateChild={updateChild}
                        removeChild={removeChild}
                        goToPage={goToPage}
                    />
                );
        }
    };

    return (
        <>
            <NavbarTwo navClass="defaultscroll sticky" manuClass="navigation-menu nav-right nav-light"/>

            <section id="page-top" className="bg-half-170 d-table w-100" style={{backgroundImage:`url(${bg2})`, backgroundPosition:'center'}}>
                <div className="bg-overlay bg-gradient-overlay"></div>
                <div className="container">
                    <div className="row mt-5 justify-content-center">
                        <div className="col-12">
                            <div className="title-heading text-center">
                                <small className="text-white-50 mb-1 fw-medium text-uppercase mx-auto">Financial Security Calculator</small>
                                <h5 className="heading fw-semibold mb-0 page-heading text-white title-dark">How much protection do you need?</h5>
                            </div>
                        </div>
                    </div>

                    <div className="position-middle-bottom">
                        <nav aria-label="breadcrumb" className="d-block">
                            <ul className="breadcrumb breadcrumb-muted mb-0 p-0">
                                <li className="breadcrumb-item"><Link to="/">HOME</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">FINANCIAL SECURITY CALCULATOR</li>
                            </ul>
                        </nav>
                    </div>
                </div>
            </section>
            
            <section className="section">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-10">
                            {/* Progress Indicator */}
                            <div id="progress-bar-section" className="card border-0 shadow rounded mb-4">
                                <div className="card-body p-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className="fw-semibold">Page {currentPage} of 6</span>
                                        <div className="progress" style={{width: '60%', height: '8px'}}>
                                            <div 
                                                className="progress-bar bg-primary" 
                                                style={{width: `${(currentPage / 6) * 100}%`}}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div id="form-section">
                            {/* Current Page Content */}
                            {getCurrentPageComponent()}

                            {/* Navigation */}
                            <div className="card border-0 shadow rounded mt-4">
                                <div className="card-body p-3">
                                    <div className="d-flex justify-content-between">
                                        <button 
                                            type="button" 
                                            className="btn btn-outline-secondary"
                                            onClick={prevPage}
                                            disabled={currentPage === 1}
                                        >
                                            <FiArrowLeft className="me-1" /> Previous
                                        </button>
                                        {currentPage < 6 && (
                                            <button 
                                                type="button" 
                                                className="btn btn-primary"
                                                onClick={nextPage}
                                            >
                                                Next <FiArrowRight className="ms-1" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Page Jumper */}
                            <div className="text-center mt-3">
                                <small className="text-muted">Jump to page: </small>
                                <div className="d-flex flex-wrap justify-content-center align-items-center mt-2">
                                    {[1, 2, 3, 4, 5, 6].map(page => (
                                        <button
                                            key={page}
                                            type="button"
                                            className={`btn btn-sm mx-1 ${currentPage === page ? 'btn-primary' : 'btn-outline-secondary'}`}
                                            onClick={() => goToPage(page)}
                                        >
                                            {page}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            
            <FooterThree/>
            <ScrollTop/>
        </>
    );
}